import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";

import {
  MessageSquare,
  Search,
  Send,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Bell,
  Inbox,
  Clock3,
  ChevronRight,
  X,
  Trash2,
  ArrowDown,
  FolderKanban,
} from "lucide-react";

import "./OssCommunicationPage.css";

import {
  fetchCommunicationThreads,
  fetchCommunicationThreadDetail,
  createThreadMessage,
  fetchCommunicationNotificationsSummary,
  markThreadAsRead,
  markAllNotificationsAsRead,
  archiveCommunicationThread,
  deleteThreadMessage,
} from "../../homebuyer/communication/communicationApi";

/* =========================================================
   FORMATTERS
   ========================================================= */

function formatDateTime(value, locale = "en-GB") {
  if (!value) return "";

  try {
    return new Date(value).toLocaleString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatTime(value, locale = "en-GB") {
  if (!value) return "";

  try {
    return new Date(value).toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function stableLower(value = "") {
  return String(value || "").toLowerCase();
}

/* =========================================================
   PARTICIPANTS
   ========================================================= */

function getParticipantDisplay(participant, t) {
  if (!participant) {
    return t("common.unknown", "Unknown");
  }

  if (participant.display_name) {
    return participant.display_name;
  }

  const fullName = `${participant.user_first_name || ""} ${
    participant.user_last_name || ""
  }`.trim();

  if (fullName) {
    return fullName;
  }

  if (participant.organisation_name) {
    return participant.organisation_name;
  }

  if (participant.user_email) {
    return participant.user_email;
  }

  return t(
    `roles.${stableLower(participant.role)}`,
    participant.role || "Unknown",
  );
}

function getExternalParticipants(thread) {
  const viewerParticipantId = thread?.viewer_participant_id;

  return (thread?.participants || []).filter((participant) => {
    if (participant.is_active === false) {
      return false;
    }

    if (
      viewerParticipantId != null &&
      String(participant.id) === String(viewerParticipantId)
    ) {
      return false;
    }

    return true;
  });
}

function getThreadOtherParticipants(thread, t) {
  const seen = new Set();

  return getExternalParticipants(thread)
    .filter((participant) => {
      const key =
        participant.organisation ||
        participant.user ||
        participant.organisation_name ||
        `${participant.role}-${participant.id}`;

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);

      return true;
    })
    .map((participant) => getParticipantDisplay(participant, t))
    .join(", ");
}

function getMainExternalParticipant(thread) {
  const participants = getExternalParticipants(thread);

  if (thread?.channel_scope === "HOMEBUYER_OSS") {
    return (
      participants.find((participant) => participant.role === "HOMEOWNER") ||
      participants[0] ||
      null
    );
  }

  if (thread?.channel_scope === "LENDER_OSS") {
    return (
      participants.find((participant) => participant.role === "LENDER") ||
      participants[0] ||
      null
    );
  }

  return participants[0] || null;
}

function getMessageParticipant(message, thread) {
  if (!message || !thread) {
    return null;
  }

  const senderId = message.sender ?? message.sender_id;

  return (
    (thread.participants || []).find(
      (participant) => String(participant.id) === String(senderId),
    ) ||
    (thread.participants || []).find(
      (participant) => participant.role === message.sender_role,
    ) ||
    null
  );
}

function getParticipantInitials(participant, t) {
  const displayName = getParticipantDisplay(participant, t);

  return (
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "?"
  );
}

function ParticipantAvatar({
  participant,
  fallbackName = "",
  size = "message",
}) {
  const displayName =
    participant?.display_name ||
    participant?.organisation_name ||
    participant?.user_email ||
    fallbackName ||
    participant?.role ||
    "Participant";

  const initials =
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "?";

  return (
    <div
      className={`oss-comm-avatar oss-comm-avatar--${size}`}
      title={displayName}
      aria-label={displayName}
    >
      {participant?.avatar_url ? (
        <img
          src={participant.avatar_url}
          alt={displayName}
          className="oss-comm-avatar__image"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <span className="oss-comm-avatar__fallback">{initials}</span>
      )}
    </div>
  );
}

/* =========================================================
   MESSAGE OWNERSHIP / GROUPING
   ========================================================= */

function isOwnMessage(message, selectedThread) {
  if (
    !message ||
    !selectedThread ||
    selectedThread.viewer_participant_id == null
  ) {
    return false;
  }

  const senderId = message.sender ?? message.sender_id;

  return String(senderId) === String(selectedThread.viewer_participant_id);
}

function isSameMinute(a, b) {
  if (!a || !b) {
    return false;
  }

  const da = new Date(a);
  const db = new Date(b);

  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate() &&
    da.getHours() === db.getHours() &&
    da.getMinutes() === db.getMinutes()
  );
}

function buildMessageGroups(messages, selectedThread) {
  const groups = [];

  messages.forEach((message) => {
    const mine = isOwnMessage(message, selectedThread);

    const senderParticipant = getMessageParticipant(message, selectedThread);

    const senderId = message.sender ?? message.sender_id;

    const lastGroup = groups[groups.length - 1];

    const lastMessage = lastGroup?.items[lastGroup.items.length - 1];

    const canAppend =
      lastGroup &&
      lastGroup.isMine === mine &&
      String(lastGroup.senderId) === String(senderId) &&
      isSameMinute(lastMessage?.created_at, message.created_at);

    if (canAppend) {
      lastGroup.items.push(message);

      return;
    }

    groups.push({
      senderId,
      senderParticipant,
      isMine: mine,
      items: [message],
    });
  });

  return groups;
}

/* =========================================================
   PROJECT GROUPING
   ========================================================= */

function getProjectId(thread) {
  return (
    thread?.group_project_id ??
    thread?.project_id ??
    thread?.project?.id ??
    null
  );
}

function getProjectCode(thread) {
  return (
    thread?.group_project_code ??
    thread?.project_code ??
    thread?.project?.code ??
    ""
  );
}

function getDossierId(thread) {
  return (
    thread?.dossier_id ??
    thread?.dossier?.id ??
    thread?.loan_case?.dossier_id ??
    thread?.loan_case?.dossier?.id ??
    thread?.project?.dossier_id ??
    thread?.project?.dossier?.id ??
    null
  );
}

function getThreadGroupKey(thread) {
  const projectId = getProjectId(thread);

  if (projectId) {
    return `project-${projectId}`;
  }

  const dossierId = getDossierId(thread);

  if (dossierId) {
    return `dossier-${dossierId}`;
  }

  return `thread-${thread.id}`;
}

function getThreadGroupLabel(thread, t) {
  const projectCode = getProjectCode(thread);

  if (projectCode) {
    return projectCode;
  }

  const projectId = getProjectId(thread);

  if (projectId) {
    return `${t("groups.project", "Renovation project")} #${projectId}`;
  }

  const dossierId = getDossierId(thread);

  if (dossierId) {
    return `${t("groups.project", "Renovation project")} #${dossierId}`;
  }

  return t("groups.other", "Other conversations");
}

/* =========================================================
   THREAD ACTOR
   ========================================================= */

function getThreadActorLabel(thread, t) {
  if (thread?.channel_scope === "HOMEBUYER_OSS") {
    return t("actors.homebuyer", "Homebuyer");
  }

  if (thread?.channel_scope === "LENDER_OSS") {
    return t("actors.lender", "Lender");
  }

  const externalParticipants = getExternalParticipants(thread);

  const hasHomebuyer = externalParticipants.some(
    (participant) => participant.role === "HOMEOWNER",
  );

  const hasLender = externalParticipants.some(
    (participant) => participant.role === "LENDER",
  );

  if (hasHomebuyer && !hasLender) {
    return t("actors.homebuyer", "Homebuyer");
  }

  if (hasLender && !hasHomebuyer) {
    return t("actors.lender", "Lender");
  }

  if (hasHomebuyer && hasLender) {
    return t("actors.project", "Project actors");
  }

  return t("actors.general", "General");
}

function getThreadActorClass(thread) {
  if (thread?.channel_scope === "HOMEBUYER_OSS") {
    return "is-homebuyer";
  }

  if (thread?.channel_scope === "LENDER_OSS") {
    return "is-lender";
  }

  const externalParticipants = getExternalParticipants(thread);

  const hasHomebuyer = externalParticipants.some(
    (participant) => participant.role === "HOMEOWNER",
  );

  const hasLender = externalParticipants.some(
    (participant) => participant.role === "LENDER",
  );

  if (hasHomebuyer && hasLender) {
    return "is-project";
  }

  if (hasHomebuyer) {
    return "is-homebuyer";
  }

  if (hasLender) {
    return "is-lender";
  }

  return "is-general";
}

function getThreadActorOrder(thread) {
  if (thread?.channel_scope === "HOMEBUYER_OSS") {
    return 1;
  }

  if (thread?.channel_scope === "LENDER_OSS") {
    return 2;
  }

  return 3;
}

/* =========================================================
   COMPONENT
   ========================================================= */

export default function OssCommunicationPage() {
  const { t, i18n } = useTranslation("oss-communication");

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const location = useLocation();

  const requestedThreadId = location.state?.threadId
    ? Number(location.state.threadId)
    : null;

  const [threads, setThreads] = useState([]);

  const [selectedThreadId, setSelectedThreadId] = useState(null);

  const [selectedThread, setSelectedThread] = useState(null);

  const [messages, setMessages] = useState([]);

  const [notificationsUnread, setNotificationsUnread] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");

  const [messageBody, setMessageBody] = useState("");

  const [isLoadingThreads, setIsLoadingThreads] = useState(true);

  const [isLoadingThreadDetail, setIsLoadingThreadDetail] = useState(false);

  const [isSendingMessage, setIsSendingMessage] = useState(false);

  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  const [messageDeleteModal, setMessageDeleteModal] = useState({
    open: false,
    messageId: null,
  });

  const [isDeletingMessage, setIsDeletingMessage] = useState(false);

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    threadId: null,
    title: "",
  });

  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    type: "",
    title: "",
    text: "",
  });

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const [lastMessageId, setLastMessageId] = useState(null);

  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState(null);

  const messagesContainerRef = useRef(null);

  const stayReadTimerRef = useRef(null);

  const scrollReadTimerRef = useRef(null);

  const isMarkingThreadReadRef = useRef(false);

  const previousSelectedThreadIdRef = useRef(null);

  const locale =
    i18n.language === "es"
      ? "es-ES"
      : i18n.language === "ca"
        ? "ca-ES"
        : i18n.language === "nl"
          ? "nl-BE"
          : "en-GB";

  const selectedThreadUnreadCount =
    threads.find((thread) => Number(thread.id) === Number(selectedThreadId))
      ?.unread_count || 0;

  async function getAccessToken() {
    return getAccessTokenSilently();
  }

  function closeFeedbackModal() {
    setFeedbackModal({
      open: false,
      type: "",
      title: "",
      text: "",
    });
  }

  function isNearBottom(container) {
    if (!container) {
      return false;
    }

    return (
      container.scrollHeight - container.scrollTop - container.clientHeight <=
      120
    );
  }

  function scrollToBottom(behavior = "smooth") {
    const container = messagesContainerRef.current;

    if (!container) {
      return;
    }

    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
  }

  /* =======================================================
     LOAD NOTIFICATIONS
     ======================================================= */

  async function loadNotificationsSummary() {
    try {
      const accessToken = await getAccessToken();

      const data = await fetchCommunicationNotificationsSummary({
        accessToken,
      });

      setNotificationsUnread(data?.unread_count || 0);
    } catch {
      // Silent refresh.
    }
  }

  /* =======================================================
     LOAD THREADS
     ======================================================= */

  async function loadThreads(options = {}) {
    const { silent = false } = options;

    try {
      if (!silent) {
        setIsLoadingThreads(true);
      }

      const accessToken = await getAccessToken();

      const data = await fetchCommunicationThreads({
        accessToken,
      });

      const nextThreads = Array.isArray(data) ? data : [];

      setThreads(nextThreads);

      if (nextThreads.length > 0) {
        setSelectedThreadId((previousId) => {
          if (
            requestedThreadId &&
            nextThreads.some(
              (thread) => Number(thread.id) === Number(requestedThreadId),
            )
          ) {
            return Number(requestedThreadId);
          }

          if (
            previousId &&
            nextThreads.some(
              (thread) => Number(thread.id) === Number(previousId),
            )
          ) {
            return previousId;
          }

          return nextThreads[0].id;
        });
      } else {
        setSelectedThreadId(null);
        setSelectedThread(null);
        setMessages([]);
        setFirstUnreadMessageId(null);
        setShowScrollToBottom(false);
      }

      await loadNotificationsSummary();
    } catch (error) {
      if (!silent) {
        setFeedbackModal({
          open: true,
          type: "error",
          title: t("messages.errorTitle", "Something went wrong"),
          text:
            error.message ||
            t("messages.loadThreadsError", "Could not load conversations."),
        });
      }
    } finally {
      if (!silent) {
        setIsLoadingThreads(false);
      }
    }
  }

  /* =======================================================
     LOAD THREAD DETAIL
     ======================================================= */

  async function loadThreadDetail(threadId, options = {}) {
    if (!threadId) {
      return;
    }

    const { silent = false } = options;

    try {
      if (!silent) {
        setIsLoadingThreadDetail(true);
      }

      const container = messagesContainerRef.current;

      const wasNearBottom = isNearBottom(container);

      const previousScrollTop = container ? container.scrollTop : 0;

      const previousScrollHeight = container ? container.scrollHeight : 0;

      const currentThread = threads.find(
        (thread) => Number(thread.id) === Number(threadId),
      );

      const unreadCount = currentThread?.unread_count || 0;

      const accessToken = await getAccessToken();

      const data = await fetchCommunicationThreadDetail({
        accessToken,
        threadId,
      });

      const nextMessages = Array.isArray(data?.messages) ? data.messages : [];

      const nextLastMessageId =
        nextMessages.length > 0
          ? nextMessages[nextMessages.length - 1].id
          : null;

      const hadMessagesBefore = messages.length > 0;

      const hasNewMessage =
        hadMessagesBefore &&
        nextLastMessageId &&
        lastMessageId &&
        String(nextLastMessageId) !== String(lastMessageId);

      let firstUnreadId = null;

      if (unreadCount > 0 && nextMessages.length >= unreadCount) {
        const unreadSlice = nextMessages.slice(-unreadCount);

        firstUnreadId = unreadSlice[0]?.id || null;
      }

      setSelectedThread(data);
      setMessages(nextMessages);
      setLastMessageId(nextLastMessageId);
      setFirstUnreadMessageId(firstUnreadId);

      requestAnimationFrame(() => {
        const currentContainer = messagesContainerRef.current;

        if (!currentContainer) {
          return;
        }

        if (!hadMessagesBefore) {
          currentContainer.scrollTop = 0;

          setShowScrollToBottom(
            unreadCount > 0 || !isNearBottom(currentContainer),
          );

          return;
        }

        if (wasNearBottom) {
          currentContainer.scrollTop = currentContainer.scrollHeight;

          setShowScrollToBottom(false);

          return;
        }

        const heightDiff = currentContainer.scrollHeight - previousScrollHeight;

        currentContainer.scrollTop = previousScrollTop + heightDiff;

        setShowScrollToBottom(hasNewMessage || !isNearBottom(currentContainer));
      });
    } catch (error) {
      if (!silent) {
        setFeedbackModal({
          open: true,
          type: "error",
          title: t("messages.errorTitle", "Something went wrong"),
          text:
            error.message ||
            t(
              "messages.loadThreadError",
              "Could not load the selected conversation.",
            ),
        });
      }
    } finally {
      if (!silent) {
        setIsLoadingThreadDetail(false);
      }
    }
  }

  /* =======================================================
     READ STATE
     ======================================================= */

  async function handleMarkSelectedThreadAsRead(trigger = "stay") {
    if (!selectedThreadId || !selectedThread) {
      return;
    }

    if (isMarkingThreadReadRef.current) {
      return;
    }

    const currentThread = threads.find(
      (thread) => Number(thread.id) === Number(selectedThreadId),
    );

    const unreadCount = currentThread?.unread_count || 0;

    if (unreadCount <= 0) {
      return;
    }

    if (!isNearBottom(messagesContainerRef.current)) {
      return;
    }

    try {
      isMarkingThreadReadRef.current = true;

      const accessToken = await getAccessToken();

      await markThreadAsRead({
        accessToken,
        threadId: selectedThreadId,
      });

      setThreads((previous) =>
        previous.map((thread) =>
          Number(thread.id) === Number(selectedThreadId)
            ? {
                ...thread,
                unread_count: 0,
              }
            : thread,
        ),
      );

      setFirstUnreadMessageId(null);

      setShowScrollToBottom(false);

      await loadNotificationsSummary();
    } catch (error) {
      console.error(`Failed to mark thread as read via ${trigger}:`, error);
    } finally {
      isMarkingThreadReadRef.current = false;
    }
  }

  /* =======================================================
     INITIAL LOAD
     ======================================================= */

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    loadThreads();
  }, [isAuthenticated]);

  /* =======================================================
     SELECTED THREAD
     ======================================================= */

  useEffect(() => {
    if (!selectedThreadId || !isAuthenticated) {
      return;
    }

    if (previousSelectedThreadIdRef.current !== selectedThreadId) {
      previousSelectedThreadIdRef.current = selectedThreadId;

      setShowScrollToBottom(false);

      setFirstUnreadMessageId(null);
    }

    if (stayReadTimerRef.current) {
      clearTimeout(stayReadTimerRef.current);
    }

    if (scrollReadTimerRef.current) {
      clearTimeout(scrollReadTimerRef.current);
    }

    loadThreadDetail(selectedThreadId);

    return () => {
      if (stayReadTimerRef.current) {
        clearTimeout(stayReadTimerRef.current);
      }

      if (scrollReadTimerRef.current) {
        clearTimeout(scrollReadTimerRef.current);
      }
    };
  }, [selectedThreadId, isAuthenticated]);

  /* =======================================================
     AUTO READ AFTER STAY
     ======================================================= */

  useEffect(() => {
    if (!selectedThreadId || !selectedThread || isLoadingThreadDetail) {
      return;
    }

    if (selectedThreadUnreadCount <= 0) {
      return;
    }

    if (stayReadTimerRef.current) {
      clearTimeout(stayReadTimerRef.current);
    }

    stayReadTimerRef.current = setTimeout(() => {
      handleMarkSelectedThreadAsRead("stay");
    }, 5500);

    return () => {
      if (stayReadTimerRef.current) {
        clearTimeout(stayReadTimerRef.current);
      }
    };
  }, [
    selectedThreadId,
    selectedThreadUnreadCount,
    selectedThread,
    isLoadingThreadDetail,
  ]);

  /* =======================================================
     READ ON SCROLL
     ======================================================= */

  useEffect(() => {
    const container = messagesContainerRef.current;

    if (!container || !selectedThreadId || !selectedThread) {
      return;
    }

    function handleMessagesScroll() {
      const nearBottom = isNearBottom(container);

      setShowScrollToBottom(!nearBottom);

      if (!nearBottom || selectedThreadUnreadCount <= 0) {
        return;
      }

      if (scrollReadTimerRef.current) {
        clearTimeout(scrollReadTimerRef.current);
      }

      scrollReadTimerRef.current = setTimeout(() => {
        handleMarkSelectedThreadAsRead("scroll");
      }, 2500);
    }

    container.addEventListener("scroll", handleMessagesScroll, {
      passive: true,
    });

    handleMessagesScroll();

    return () => {
      container.removeEventListener("scroll", handleMessagesScroll);

      if (scrollReadTimerRef.current) {
        clearTimeout(scrollReadTimerRef.current);
      }
    };
  }, [selectedThreadId, selectedThread, selectedThreadUnreadCount]);

  /* =======================================================
     FILTER THREADS
     ======================================================= */

  const filteredThreads = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      return threads;
    }

    return threads.filter((thread) => {
      const projectCode = (getProjectCode(thread) || "").toLowerCase();

      const projectId = String(getProjectId(thread) || "");

      const participantText = (
        getThreadOtherParticipants(thread, t) || ""
      ).toLowerCase();

      const titleText = (thread.title || "").toLowerCase();

      const latestMessageText = (
        thread.latest_message?.body || ""
      ).toLowerCase();

      return (
        projectCode.includes(term) ||
        projectId.includes(term) ||
        participantText.includes(term) ||
        titleText.includes(term) ||
        latestMessageText.includes(term)
      );
    });
  }, [threads, searchTerm, t]);

  /* =======================================================
     GROUP BY RENOVATION PROJECT
     ======================================================= */

  const groupedThreads = useMemo(() => {
    const groupsMap = new Map();

    filteredThreads.forEach((thread) => {
      const key = getThreadGroupKey(thread);
      const label = getThreadGroupLabel(thread, t);

      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          key,
          label,
          projectId: getProjectId(thread),
          projectCode: getProjectCode(thread),
          items: [],
          unreadCount: 0,
        });
      }

      const group = groupsMap.get(key);

      group.items.push(thread);

      group.unreadCount += thread.unread_count || 0;
    });

    return Array.from(groupsMap.values())
      .map((group) => ({
        ...group,

        items: [...group.items].sort((a, b) => {
          const actorDifference =
            getThreadActorOrder(a) - getThreadActorOrder(b);

          if (actorDifference !== 0) {
            return actorDifference;
          }

          const aDate = new Date(
            a.latest_message?.created_at || a.updated_at || 0,
          ).getTime();

          const bDate = new Date(
            b.latest_message?.created_at || b.updated_at || 0,
          ).getTime();

          return bDate - aDate;
        }),
      }))
      .sort((a, b) => {
        const aLatest = Math.max(
          ...a.items.map((item) =>
            new Date(
              item.latest_message?.created_at || item.updated_at || 0,
            ).getTime(),
          ),
        );

        const bLatest = Math.max(
          ...b.items.map((item) =>
            new Date(
              item.latest_message?.created_at || item.updated_at || 0,
            ).getTime(),
          ),
        );

        return bLatest - aLatest;
      });
  }, [filteredThreads, t]);

  /* =======================================================
     SEND MESSAGE
     ======================================================= */

  async function handleSendMessage(event) {
    event.preventDefault();

    const trimmed = messageBody.trim();

    if (!trimmed || !selectedThreadId) {
      return;
    }

    try {
      setIsSendingMessage(true);

      const accessToken = await getAccessToken();

      await createThreadMessage({
        accessToken,
        threadId: selectedThreadId,
        body: trimmed,
        messageType: "COMMENT",
        visibility: "ALL",
      });

      setMessageBody("");

      await loadThreadDetail(selectedThreadId, {
        silent: true,
      });

      await loadThreads({
        silent: true,
      });

      requestAnimationFrame(() => {
        scrollToBottom("smooth");
      });
    } catch (error) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t("messages.sendError", "Could not send your message."),
      });
    } finally {
      setIsSendingMessage(false);
    }
  }

  function handleComposerKeyDown(event) {
    const isSendShortcut =
      event.key === "Enter" && (event.ctrlKey || event.metaKey);

    if (!isSendShortcut) {
      return;
    }

    if (event.nativeEvent?.isComposing) {
      return;
    }

    event.preventDefault();

    if (isSendingMessage || !messageBody.trim() || !selectedThreadId) {
      return;
    }

    event.currentTarget.form?.requestSubmit();
  }

  /* =======================================================
     MARK ALL READ
     ======================================================= */

  async function handleMarkAllNotificationsRead() {
    try {
      setIsMarkingAllRead(true);

      const accessToken = await getAccessToken();

      await markAllNotificationsAsRead({
        accessToken,
      });

      setNotificationsUnread(0);

      setThreads((previous) =>
        previous.map((thread) => ({
          ...thread,
          unread_count: 0,
        })),
      );

      setFirstUnreadMessageId(null);
    } catch (error) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t(
            "messages.markAllReadError",
            "Could not mark notifications as read.",
          ),
      });
    } finally {
      setIsMarkingAllRead(false);
    }
  }

  /* =======================================================
     DELETE THREAD
     ======================================================= */

  async function confirmDeleteThread() {
    if (!deleteModal.threadId) {
      return;
    }

    try {
      const accessToken = await getAccessToken();

      await archiveCommunicationThread({
        accessToken,
        threadId: deleteModal.threadId,
      });

      const updatedThreads = threads.filter(
        (thread) => Number(thread.id) !== Number(deleteModal.threadId),
      );

      setThreads(updatedThreads);

      if (Number(selectedThreadId) === Number(deleteModal.threadId)) {
        if (updatedThreads.length > 0) {
          setSelectedThreadId(updatedThreads[0].id);
        } else {
          setSelectedThreadId(null);

          setSelectedThread(null);

          setMessages([]);
        }
      }

      setDeleteModal({
        open: false,
        threadId: null,
        title: "",
      });

      setFeedbackModal({
        open: true,
        type: "success",
        title: t("messages.successTitle", "Success"),
        text: t(
          "messages.threadDeleted",
          "The conversation was deleted successfully.",
        ),
      });

      await loadNotificationsSummary();
    } catch (error) {
      setDeleteModal({
        open: false,
        threadId: null,
        title: "",
      });

      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t("messages.deleteThreadError", "Could not delete the conversation."),
      });
    }
  }

  /* =======================================================
     DELETE MESSAGE
     ======================================================= */

  async function confirmDeleteMessage() {
    const messageId = messageDeleteModal.messageId;

    const threadId = selectedThreadId;

    if (!messageId || !threadId) {
      return;
    }

    try {
      setIsDeletingMessage(true);

      const accessToken = await getAccessToken();

      await deleteThreadMessage({
        accessToken,
        threadId,
        messageId,
      });

      setMessageDeleteModal({
        open: false,
        messageId: null,
      });

      await loadThreadDetail(threadId, {
        silent: true,
      });

      await loadThreads({
        silent: true,
      });
    } catch (error) {
      console.error("Failed to delete communication message:", error);

      setMessageDeleteModal({
        open: false,
        messageId: null,
      });

      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t("messages.deleteMessageError", "The message could not be deleted."),
      });
    } finally {
      setIsDeletingMessage(false);
    }
  }

  function openMessageDeleteModal(messageId) {
    setFeedbackModal({
      open: false,
      type: "",
      title: "",
      text: "",
    });

    setDeleteModal({
      open: false,
      threadId: null,
      title: "",
    });

    setMessageDeleteModal({
      open: true,
      messageId,
    });
  }

  /* =======================================================
     POLLING
     ======================================================= */

  useEffect(() => {
    if (!selectedThreadId || !isAuthenticated) {
      return;
    }

    const interval = setInterval(async () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      try {
        await loadThreadDetail(selectedThreadId, {
          silent: true,
        });

        await loadThreads({
          silent: true,
        });
      } catch (error) {
        console.error("Polling OSS communication thread failed:", error);
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [selectedThreadId, isAuthenticated]);

  /* =======================================================
     SCROLL TO UNREAD
     ======================================================= */

  function handleScrollToUnreadOrBottom() {
    if (firstUnreadMessageId) {
      const target = document.getElementById(
        `oss-comm-message-anchor-${firstUnreadMessageId}`,
      );

      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        return;
      }
    }

    scrollToBottom("smooth");
  }

  /* =======================================================
     SELECTED PARTICIPANT
     ======================================================= */

  const selectedMainParticipant = getMainExternalParticipant(selectedThread);

  const selectedAvatarUrl = selectedMainParticipant?.avatar_url || "";

  const selectedAvatarInitials = getParticipantInitials(
    selectedMainParticipant,
    t,
  );

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <>
      <div className="oss-comm-page">
        <div className="oss-comm-page__shell">
          {/* HERO */}

          <header className="oss-comm-hero">
            <div>
              <p className="oss-comm-hero__eyebrow">
                {t("hero.eyebrow", "Communication hub")}
              </p>

              <h1 className="oss-comm-hero__title">
                {t("hero.title", "Messages & coordination")}
              </h1>

              <p className="oss-comm-hero__subtitle">
                {t(
                  "hero.subtitle",
                  "Follow conversations across renovation projects, homebuyers and lenders in one place.",
                )}
              </p>
            </div>

            <div className="oss-comm-hero__meta">
              <div className="oss-comm-hero__meta-card">
                <span>{t("hero.totalThreads", "Threads")}</span>

                <strong>{threads.length}</strong>
              </div>

              <div className="oss-comm-hero__meta-card">
                <span>{t("hero.unreadNotifications", "Unread")}</span>

                <strong>{notificationsUnread}</strong>
              </div>

              <button
                type="button"
                className="oss-comm-hero__meta-btn"
                onClick={handleMarkAllNotificationsRead}
                disabled={isMarkingAllRead || notificationsUnread === 0}
              >
                <Bell size={15} />

                {isMarkingAllRead
                  ? t("buttons.marking", "Marking...")
                  : t("buttons.markAllRead", "Mark all as read")}
              </button>
            </div>
          </header>

          <div className="oss-comm-layout">
            {/* SIDEBAR */}

            <aside className="oss-comm-sidebar">
              <div className="oss-comm-sidebar__top">
                <div className="oss-comm-search">
                  <Search size={16} />

                  <input
                    type="text"
                    placeholder={t(
                      "search.placeholder",
                      "Search conversations...",
                    )}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                </div>
              </div>

              <div className="oss-comm-sidebar__list">
                {isLoadingThreads ? (
                  <div className="oss-comm-empty">
                    <Loader2 size={18} className="oss-comm-spin" />

                    <span>
                      {t("messages.loadingThreads", "Loading conversations...")}
                    </span>
                  </div>
                ) : groupedThreads.length === 0 ? (
                  <div className="oss-comm-empty">
                    <Inbox size={18} />

                    <span>
                      {t("messages.noThreads", "No conversations found.")}
                    </span>
                  </div>
                ) : (
                  groupedThreads.map((group) => (
                    <div className="oss-comm-group" key={group.key}>
                      <div className="oss-comm-group__header">
                        <div className="oss-comm-group__title-wrap">
                          <FolderKanban size={14} />

                          <div className="oss-comm-group__title-block">
                            <span className="oss-comm-group__title">
                              {group.label}
                            </span>

                            <span className="oss-comm-group__subtitle">
                              {group.items.length}{" "}
                              {group.items.length === 1
                                ? t("groups.threadSingle", "conversation")
                                : t("groups.threadPlural", "conversations")}
                            </span>
                          </div>
                        </div>

                        {group.unreadCount > 0 ? (
                          <span className="oss-comm-group__badge">
                            {group.unreadCount}
                          </span>
                        ) : null}
                      </div>

                      <div className="oss-comm-group__items">
                        {group.items.map((thread, index) => {
                          const isSelected =
                            Number(selectedThreadId) === Number(thread.id);

                          const actorLabel = getThreadActorLabel(thread, t);

                          const actorClass = getThreadActorClass(thread);

                          const mainParticipant =
                            getMainExternalParticipant(thread);

                          const avatarUrl = mainParticipant?.avatar_url || "";

                          const avatarInitials = getParticipantInitials(
                            mainParticipant,
                            t,
                          );

                          return (
                            <React.Fragment key={thread.id}>
                              <button
                                type="button"
                                className={`oss-comm-thread-card ${
                                  isSelected ? "is-selected" : ""
                                }`}
                                onClick={() => setSelectedThreadId(thread.id)}
                              >
                                <div className="oss-comm-thread-card__top">
                                  <div className="oss-comm-thread-card__avatar">
                                    {avatarUrl ? (
                                      <img
                                        src={avatarUrl}
                                        alt={getParticipantDisplay(
                                          mainParticipant,
                                          t,
                                        )}
                                      />
                                    ) : (
                                      <span>{avatarInitials}</span>
                                    )}
                                  </div>

                                  <div className="oss-comm-thread-card__main">
                                    <div className="oss-comm-thread-card__title-row">
                                      <span
                                        className={`oss-comm-thread-card__actor-badge ${actorClass}`}
                                      >
                                        {actorLabel}
                                      </span>

                                      {thread.unread_count > 0 ? (
                                        <span className="oss-comm-thread-card__badge">
                                          {thread.unread_count}
                                        </span>
                                      ) : null}
                                    </div>

                                    <h3 className="oss-comm-thread-card__title">
                                      {thread.title}
                                    </h3>

                                    <p className="oss-comm-thread-card__type">
                                      {t(
                                        `threadTypes.${thread.thread_type}`,
                                        thread.thread_type,
                                      )}
                                    </p>
                                  </div>

                                  <ChevronRight size={16} />
                                </div>

                                <p className="oss-comm-thread-card__participants">
                                  {getThreadOtherParticipants(thread, t) ||
                                    t(
                                      "common.noParticipants",
                                      "No participants",
                                    )}
                                </p>

                                <p className="oss-comm-thread-card__preview">
                                  {thread.latest_message?.body ||
                                    t(
                                      "common.noMessagesYet",
                                      "No messages yet.",
                                    )}
                                </p>

                                <div className="oss-comm-thread-card__bottom">
                                  <span className="oss-comm-thread-card__status">
                                    {t(
                                      `statuses.${thread.status}`,
                                      thread.status || "OPEN",
                                    )}
                                  </span>

                                  <span className="oss-comm-thread-card__time">
                                    <Clock3 size={12} />

                                    {formatDateTime(
                                      thread.latest_message?.created_at ||
                                        thread.updated_at,
                                      locale,
                                    )}
                                  </span>
                                </div>
                              </button>

                              {index < group.items.length - 1 ? (
                                <div className="oss-comm-group__divider" />
                              ) : null}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </aside>

            {/* MAIN CONVERSATION */}

            <section className="oss-comm-main">
              {!selectedThreadId ? (
                <div className="oss-comm-main__empty">
                  <MessageSquare size={28} />

                  <h2>{t("empty.title", "Select a conversation")}</h2>

                  <p>
                    {t(
                      "empty.text",
                      "Choose a conversation from the left to view messages.",
                    )}
                  </p>
                </div>
              ) : isLoadingThreadDetail ? (
                <div className="oss-comm-main__empty">
                  <Loader2 size={22} className="oss-comm-spin" />

                  <h2>
                    {t("messages.loadingThread", "Loading conversation...")}
                  </h2>
                </div>
              ) : (
                <>
                  {/* THREAD HEADER */}

                  <div className="oss-comm-thread-header">
                    <div className="oss-comm-thread-header__top">
                      <div className="oss-comm-thread-header__identity">
                        <div className="oss-comm-thread-header__title-wrap">
                          <div className="oss-comm-thread-header__title-row">
                            <h2 className="oss-comm-thread-header__title">
                              {selectedThread?.title ||
                                t("common.thread", "Conversation")}
                            </h2>

                            {selectedThreadUnreadCount > 0 ? (
                              <span className="oss-comm-thread-header__unread-badge">
                                {selectedThreadUnreadCount}
                              </span>
                            ) : null}
                          </div>

                          <div className="oss-comm-thread-header__meta">
                            <span>
                              {getThreadGroupLabel(selectedThread, t)}
                            </span>

                            <span>•</span>

                            <span>
                              {getThreadActorLabel(selectedThread, t)}
                            </span>

                            <span>•</span>

                            <span>
                              {t(
                                `threadTypes.${selectedThread?.thread_type}`,
                                selectedThread?.thread_type || "GENERAL",
                              )}
                            </span>

                            <span>•</span>

                            <span>
                              {t(
                                `statuses.${selectedThread?.status}`,
                                selectedThread?.status || "OPEN",
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="oss-comm-delete-icon-btn"
                        onClick={() =>
                          setDeleteModal({
                            open: true,
                            threadId: selectedThread?.id || null,
                            title: selectedThread?.title || "",
                          })
                        }
                        title={t("buttons.deleteThread", "Delete conversation")}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="oss-comm-thread-header__participants">
                      {Array.from(
                        new Map(
                          getExternalParticipants(selectedThread).map(
                            (participant) => {
                              const key =
                                participant.organisation ||
                                participant.user ||
                                participant.organisation_name ||
                                participant.user_email ||
                                participant.id;

                              return [key, participant];
                            },
                          ),
                        ).values(),
                      ).map((participant) => (
                        <div
                          className="oss-comm-thread-header__participant"
                          key={participant.id}
                        >
                          <ParticipantAvatar
                            participant={participant}
                            size="header"
                          />

                          <span>{getParticipantDisplay(participant, t)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* MESSAGES */}

                  <div className="oss-comm-messages-wrapper">
                    <div
                      className="oss-comm-messages"
                      ref={messagesContainerRef}
                    >
                      {messages.length === 0 ? (
                        <div className="oss-comm-main__empty oss-comm-main__empty--messages">
                          <Inbox size={22} />

                          <h3>{t("messages.noMessages", "No messages yet")}</h3>

                          <p>
                            {t(
                              "messages.noMessagesText",
                              "Start the conversation by sending your first message below.",
                            )}
                          </p>
                        </div>
                      ) : (
                        buildMessageGroups(messages, selectedThread).map(
                          (group, groupIndex) => (
                            <div
                              key={`group-${groupIndex}-${group.items[0]?.id}`}
                              className={`oss-comm-message-row ${
                                group.isMine
                                  ? "oss-comm-message-row--mine"
                                  : "oss-comm-message-row--other"
                              }`}
                            >
                              {!group.isMine ? (
                                <ParticipantAvatar
                                  participant={group.senderParticipant}
                                  fallbackName={group.items[0]?.sender_display}
                                  size="message"
                                />
                              ) : null}

                              <div
                                className={`oss-comm-message-group ${
                                  group.isMine
                                    ? "oss-comm-message-group--mine"
                                    : "oss-comm-message-group--other"
                                }`}
                              >
                                {group.items.map((message, itemIndex) => {
                                  const showUnreadSeparator =
                                    firstUnreadMessageId &&
                                    String(message.id) ===
                                      String(firstUnreadMessageId) &&
                                    !group.isMine;

                                  const isFirst = itemIndex === 0;

                                  const isLast =
                                    itemIndex === group.items.length - 1;

                                  const isDeleted = Boolean(message.is_deleted);

                                  return (
                                    <React.Fragment key={message.id}>
                                      {showUnreadSeparator ? (
                                        <div className="oss-comm-unread-separator">
                                          <span className="oss-comm-unread-separator__line" />

                                          <span className="oss-comm-unread-separator__label">
                                            {t(
                                              "messages.newMessages",
                                              "New messages",
                                            )}
                                          </span>

                                          <span className="oss-comm-unread-separator__line" />
                                        </div>
                                      ) : null}

                                      <div
                                        id={`oss-comm-message-anchor-${message.id}`}
                                        className={`oss-comm-message ${
                                          group.isMine
                                            ? "oss-comm-message--mine"
                                            : "oss-comm-message--other"
                                        } ${
                                          isFirst
                                            ? "oss-comm-message--group-first"
                                            : ""
                                        } ${
                                          isLast
                                            ? "oss-comm-message--group-last"
                                            : ""
                                        } ${
                                          !isFirst && !isLast
                                            ? "oss-comm-message--group-middle"
                                            : ""
                                        } ${
                                          group.items.length === 1
                                            ? "oss-comm-message--single"
                                            : ""
                                        }`}
                                      >
                                        {isFirst ? (
                                          <div className="oss-comm-message__meta">
                                            <span className="oss-comm-message__sender">
                                              {message.sender_display ||
                                                getParticipantDisplay(
                                                  group.senderParticipant,
                                                  t,
                                                )}
                                            </span>

                                            <span className="oss-comm-message__type">
                                              {t(
                                                `messageTypes.${message.message_type}`,
                                                message.message_type,
                                              )}
                                            </span>

                                            <span className="oss-comm-message__time">
                                              {formatTime(
                                                message.created_at,
                                                locale,
                                              )}
                                            </span>
                                          </div>
                                        ) : null}

                                        <div
                                          className={`oss-comm-message__body ${
                                            isDeleted
                                              ? "oss-comm-message__body--deleted"
                                              : ""
                                          }`}
                                        >
                                          {isDeleted
                                            ? group.isMine
                                              ? t(
                                                  "messages.youDeletedMessage",
                                                  "You deleted this message.",
                                                )
                                              : t(
                                                  "messages.deletedMessage",
                                                  "This message was deleted.",
                                                )
                                            : message.body ||
                                              t(
                                                "common.emptyMessage",
                                                "Empty message",
                                              )}
                                        </div>

                                        {group.isMine &&
                                        !isDeleted &&
                                        !["SYSTEM", "STATUS_CHANGE"].includes(
                                          message.message_type,
                                        ) ? (
                                          <button
                                            type="button"
                                            className="oss-comm-message__delete"
                                            onClick={(event) => {
                                              event.stopPropagation();

                                              openMessageDeleteModal(
                                                message.id,
                                              );
                                            }}
                                            title={t(
                                              "buttons.deleteMessage",
                                              "Delete message",
                                            )}
                                            aria-label={t(
                                              "buttons.deleteMessage",
                                              "Delete message",
                                            )}
                                          >
                                            <Trash2 size={13} />
                                          </button>
                                        ) : null}

                                        {!isFirst ? (
                                          <div className="oss-comm-message__time oss-comm-message__time--inline">
                                            {formatTime(
                                              message.created_at,
                                              locale,
                                            )}
                                          </div>
                                        ) : null}
                                      </div>
                                    </React.Fragment>
                                  );
                                })}
                              </div>

                              {group.isMine ? (
                                <ParticipantAvatar
                                  participant={group.senderParticipant}
                                  fallbackName={group.items[0]?.sender_display}
                                  size="message"
                                />
                              ) : null}
                            </div>
                          ),
                        )
                      )}
                    </div>

                    {showScrollToBottom ? (
                      <button
                        type="button"
                        className="oss-comm-scroll-to-bottom"
                        onClick={handleScrollToUnreadOrBottom}
                      >
                        <ArrowDown size={15} />

                        {selectedThreadUnreadCount > 0
                          ? t("buttons.scrollToUnread", "Go to new messages")
                          : t("buttons.scrollToLatest", "Go to latest")}
                      </button>
                    ) : null}
                  </div>

                  {/* COMPOSER */}

                  <form
                    className="oss-comm-composer"
                    onSubmit={handleSendMessage}
                  >
                    <textarea
                      rows="3"
                      value={messageBody}
                      onChange={(event) => setMessageBody(event.target.value)}
                      onKeyDown={handleComposerKeyDown}
                      placeholder={t(
                        "composer.placeholder",
                        "Write your message here...",
                      )}
                    />

                    <div className="oss-comm-composer__actions">
                      <span className="oss-comm-composer__hint">
                        {t(
                          "composer.hint",
                          "Messages are visible to the relevant actors in this conversation.",
                        )}
                      </span>

                      <button
                        type="submit"
                        className="oss-comm-send-btn"
                        disabled={isSendingMessage || !messageBody.trim()}
                      >
                        {isSendingMessage ? (
                          <>
                            <Loader2 size={15} className="oss-comm-spin" />

                            {t("buttons.sending", "Sending...")}
                          </>
                        ) : (
                          <>
                            <Send size={15} />

                            {t("buttons.send", "Send")}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* FEEDBACK MODAL */}

      {feedbackModal.open ? (
        <div className="oss-comm-modal-overlay" onClick={closeFeedbackModal}>
          <div
            className={`oss-comm-modal ${
              feedbackModal.type === "error"
                ? "oss-comm-modal--error"
                : "oss-comm-modal--success"
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="oss-comm-modal__icon">
              {feedbackModal.type === "error" ? (
                <AlertCircle size={22} />
              ) : (
                <CheckCircle2 size={22} />
              )}
            </div>

            <div className="oss-comm-modal__content">
              <h3>{feedbackModal.title}</h3>

              <p>{feedbackModal.text}</p>
            </div>

            <button
              type="button"
              className="oss-comm-modal__close"
              onClick={closeFeedbackModal}
              aria-label={t("common.close", "Close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}

      {/* DELETE THREAD MODAL */}

      {deleteModal.open ? (
        <div
          className="oss-comm-modal-overlay"
          onClick={() =>
            setDeleteModal({
              open: false,
              threadId: null,
              title: "",
            })
          }
        >
          <div
            className="oss-comm-delete-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="oss-comm-delete-modal__icon">
              <Trash2 size={20} />
            </div>

            <div className="oss-comm-delete-modal__content">
              <h3>{t("deleteModal.title", "Delete conversation?")}</h3>

              <p>
                {t(
                  "deleteModal.text",
                  "This conversation will be removed from your active conversations.",
                )}
              </p>

              {deleteModal.title ? (
                <div className="oss-comm-delete-modal__thread-name">
                  {deleteModal.title}
                </div>
              ) : null}
            </div>

            <div className="oss-comm-delete-modal__actions">
              <button
                type="button"
                className="oss-comm-delete-modal__cancel"
                onClick={() =>
                  setDeleteModal({
                    open: false,
                    threadId: null,
                    title: "",
                  })
                }
              >
                {t("buttons.cancel", "Cancel")}
              </button>

              <button
                type="button"
                className="oss-comm-delete-modal__confirm"
                onClick={confirmDeleteThread}
              >
                <Trash2 size={15} />

                {t("buttons.delete", "Delete")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* DELETE MESSAGE MODAL */}

      {messageDeleteModal.open ? (
        <div
          className="oss-comm-modal-overlay"
          onClick={() =>
            setMessageDeleteModal({
              open: false,
              messageId: null,
            })
          }
        >
          <div
            className="oss-comm-delete-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="oss-comm-delete-modal__icon">
              <Trash2 size={20} />
            </div>

            <div className="oss-comm-delete-modal__content">
              <h3>{t("deleteMessageModal.title", "Delete message?")}</h3>

              <p>
                {t(
                  "deleteMessageModal.text",
                  "The message will remain in the conversation as deleted, but its content will no longer be visible.",
                )}
              </p>
            </div>

            <div className="oss-comm-delete-modal__actions">
              <button
                type="button"
                className="oss-comm-delete-modal__cancel"
                disabled={isDeletingMessage}
                onClick={() =>
                  setMessageDeleteModal({
                    open: false,
                    messageId: null,
                  })
                }
              >
                {t("buttons.cancel", "Cancel")}
              </button>

              <button
                type="button"
                className="oss-comm-delete-modal__confirm"
                disabled={isDeletingMessage}
                onClick={confirmDeleteMessage}
              >
                {isDeletingMessage ? (
                  <>
                    <Loader2 size={15} className="oss-comm-spin" />

                    {t("buttons.deleting", "Deleting...")}
                  </>
                ) : (
                  <>
                    <Trash2 size={15} />

                    {t("buttons.delete", "Delete")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
